import { motion } from 'motion/react';
import { ArrowLeft, Calendar, MapPin, DollarSign, Briefcase, Users, Award, Share2, CheckCircle, Copy } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from "sonner";
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { useStickyScroll } from './hooks/useStickyScroll';
import type { JobData } from '../types/job';
import { filterActiveJobs, formatPostedDate, formatExpirationDate } from '../types/job';
import { JobExpirationCountdown } from './JobExpirationCountdown';
import { JobApplicationModal } from './JobApplicationModal';
import { useState } from 'react';

interface JobDetailTemplateProps {
  job: JobData;
  allJobs?: JobData[];
  enhanced?: boolean;
  stickyTopOffset?: string;
}

export function JobDetailTemplate({ 
  job, 
  allJobs = [], 
  enhanced = true,
  stickyTopOffset = 'top-24'
}: JobDetailTemplateProps) {
  const navigate = useNavigate();
  const [isApplicationModalOpen, setIsApplicationModalOpen] = useState(false);
  
  // Initialize sticky scroll hook for enhanced mode
  const { isSticky, stickyRef, scrollProgress } = useStickyScroll({
    offset: 96, // 6rem offset for header
    containerSelector: 'main',
    disableOnMobile: true
  });
  
  // Filter related jobs to exclude expired positions and current job
  const relatedJobs = filterActiveJobs(allJobs).filter(j => 
    j.id !== job.id && 
    (j.department === job.department || j.location === job.location)
  ).slice(0, 3);

  const handleShare = async () => {
    const currentUrl = window.location.href;
    const shareData = {
      title: `${job.title} - amasQIS`,
      text: `Check out this job opportunity: ${job.description}`,
      url: currentUrl,
    };

    try {
      // Try native Web Share API first (mobile devices)
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success('Job shared successfully!', {
          description: 'Thank you for sharing this opportunity',
          icon: <CheckCircle className="w-4 h-4" />,
        });
      } else {
        // Fallback to clipboard copy
        await navigator.clipboard.writeText(currentUrl);
        toast.success('Job URL copied to clipboard!', {
          description: 'You can now paste and share this job link',
          icon: <Copy className="w-4 h-4" />,
        });
      }
    } catch (error) {
      // Handle clipboard API failures
      try {
        // Fallback method for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = currentUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        toast.success('Job URL copied to clipboard!', {
          description: 'You can now paste and share this job link',
          icon: <Copy className="w-4 h-4" />,
        });
      } catch (fallbackError) {
        toast.error('Unable to copy URL', {
          description: 'Please manually copy the URL from your browser address bar',
        });
      }
    }
  };

  const handleApply = () => {
    // Open the job application modal
    setIsApplicationModalOpen(true);
  };

  // Sidebar Component - Reusable for both layouts
  const JobSidebar = ({ className = "", shouldUseSticky = false }: { 
    className?: string; 
    shouldUseSticky?: boolean; 
  }) => {
    const sidebarContent = (
      <div className="space-y-6">
        {/* Enhanced Sticky Mode: Reading Progress Bar */}
        {enhanced && shouldUseSticky && isSticky && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full h-1 bg-border/30 rounded-full overflow-hidden mb-4"
          >
            <motion.div 
              className="h-full bg-linear-to-r from-primary to-accent transition-all duration-300 ease-out"
              style={{ width: `${scrollProgress * 100}%` }}
            />
          </motion.div>
        )}

        {/* Job Quick Facts */}
        <Card className={`glass p-6 transition-all duration-300 ${
          enhanced && shouldUseSticky && isSticky ? 'ring-1 ring-primary/20 shadow-lg shadow-primary/10' : ''
        }`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center">
              <Briefcase className="w-4 h-4 mr-2" />
              Job Details
            </h3>
            {job.expirationDate && (
              <JobExpirationCountdown 
                expirationDate={job.expirationDate}
                variant="outline"
                className="text-xs"
              />
            )}
          </div>
          <div className="space-y-4">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Department</div>
              <div className="font-medium">{job.department}</div>
            </div>
            <Separator />
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Location</div>
              <div className="font-medium">{job.location}</div>
            </div>
            <Separator />
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Work Mode</div>
              <div className="font-medium">{job.workMode}</div>
            </div>
            <Separator />
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Employment Type</div>
              <div className="font-medium">{job.type}</div>
            </div>
            <Separator />
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Experience</div>
              <div className="font-medium">{job.experience}</div>
            </div>
            <Separator />
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Salary</div>
              <div className="font-medium text-primary">{job.salary}</div>
            </div>
            {job.expirationDate && (
              <>
                <Separator />
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Expires</div>
                  <div className="font-medium text-muted-foreground">{formatExpirationDate(job.expirationDate)}</div>
                  <div className="mt-1">
                    <JobExpirationCountdown 
                      expirationDate={job.expirationDate}
                      variant="outline"
                      className="text-xs"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Apply Card with Enhanced Sticky Behavior */}
        <Card className={`glass p-6 text-center transition-all duration-300 ${
          enhanced && shouldUseSticky && isSticky ? 'ring-1 ring-primary/20 shadow-lg shadow-primary/10' : ''
        }`}>
          <h3 className="font-semibold mb-4">Ready to Apply?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Join our team and help shape the future of AI
          </p>
          <Button
            onClick={handleApply}
            className={`w-full bg-primary text-primary-foreground hover:neon-glow transition-all duration-300 cursor-pointer ${
              enhanced && shouldUseSticky && isSticky ? 'transform hover:scale-105' : ''
            }`}
          >
            Apply Now
            <Briefcase className="ml-2 w-4 h-4" />
          </Button>
          
          {/* Reading Progress Indicator when sticky and enhanced */}
          {enhanced && shouldUseSticky && isSticky && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 text-xs text-muted-foreground"
            >
              Reading Progress: {Math.round(scrollProgress * 100)}%
            </motion.div>
          )}
        </Card>

        {/* Contact Info */}
        <Card className={`glass p-6 transition-all duration-300 ${
          enhanced && shouldUseSticky && isSticky ? 'ring-1 ring-primary/20' : ''
        }`}>
          <h3 className="font-semibold mb-4">Have Questions?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Reach out to our recruitment team
          </p>
          <div className="space-y-2 text-sm">
            <div>
              <strong>Email:</strong>{' '}
              <a href="mailto:careers@amasqis.ai" className="text-primary hover:text-primary/80 transition-colors">
                careers@amasqis.ai
              </a>
            </div>
          </div>
        </Card>
      </div>
    );

    // Enhanced sticky mode with useStickyScroll hook
    if (enhanced && shouldUseSticky) {
      return (
        <div 
          ref={stickyRef}
          className={`${className} transition-all duration-300 ${
            isSticky ? 'transform-gpu' : ''
          }`}
          style={{
            transform: isSticky ? 'translateY(0)' : 'none',
            opacity: enhanced ? Math.max(0.85, 1 - (scrollProgress * 0.15)) : 1, // Subtle fade based on scroll progress
          }}
        >
          {sidebarContent}
        </div>
      );
    }

    // Standard sticky mode
    return (
      <div className={`${className} lg:sticky lg:${stickyTopOffset} lg:h-fit`}>
        {sidebarContent}
      </div>
    );
  };

  // Main Content Component - Reusable
  const JobMainContent = () => (
    <div className="space-y-8">
      {/* Job Description */}
      <Card className="glass p-6 md:p-8">
        <h2 className="text-2xl font-semibold mb-4">About This Role</h2>
        <p className="text-muted-foreground leading-relaxed">
          {job.jobSummary}
        </p>
      </Card>

      {/* Team Info */}
      <Card className="glass p-6 md:p-8">
        <h2 className="text-2xl font-semibold mb-4 flex items-center">
          <Users className="w-6 h-6 mr-2 text-primary" />
          About the Team
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          {job.teamInfo}
        </p>
      </Card>

      {/* Responsibilities */}
      <Card className="glass p-6 md:p-8">
        <h2 className="text-2xl font-semibold mb-6">Key Responsibilities</h2>
        <ul className="space-y-4">
          {job.responsibilities.map((responsibility, index) => (
            <li key={index} className="flex items-start">
              <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-4 shrink-0" />
              <span className="text-muted-foreground leading-relaxed">{responsibility}</span>
            </li>
          ))}
        </ul>
      </Card>

      {/* Requirements */}
      <Card className="glass p-6 md:p-8">
        <h2 className="text-2xl font-semibold mb-6">Requirements</h2>
        <ul className="space-y-4">
          {job.requirements.map((requirement, index) => (
            <li key={index} className="flex items-start">
              <div className="w-2 h-2 bg-destructive rounded-full mt-2 mr-4 shrink-0" />
              <span className="text-muted-foreground leading-relaxed">{requirement}</span>
            </li>
          ))}
        </ul>
      </Card>

      {/* Preferred Qualifications */}
      {job.preferredQualifications.length > 0 && (
        <Card className="glass p-6 md:p-8">
          <h2 className="text-2xl font-semibold mb-6">Preferred Qualifications</h2>
          <ul className="space-y-4">
            {job.preferredQualifications.map((qualification, index) => (
              <li key={index} className="flex items-start">
                <div className="w-2 h-2 bg-accent rounded-full mt-2 mr-4 shrink-0" />
                <span className="text-muted-foreground leading-relaxed">{qualification}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Benefits */}
      <Card className="glass p-6 md:p-8">
        <h2 className="text-2xl font-semibold mb-6 flex items-center">
          <Award className="w-6 h-6 mr-2 text-primary" />
          Benefits & Perks
        </h2>
        <ul className="space-y-4">
          {job.benefits.map((benefit, index) => (
            <li key={index} className="flex items-start">
              <div className="w-2 h-2 bg-secondary rounded-full mt-2 mr-4 shrink-0" />
              <span className="text-muted-foreground leading-relaxed">{benefit}</span>
            </li>
          ))}
        </ul>
      </Card>

      {/* Related Jobs */}
      {relatedJobs.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Card className="glass p-6 md:p-8">
            <h2 className="text-2xl font-semibold mb-6">Related Opportunities</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedJobs.map((relatedJob) => (
                <Card key={relatedJob.id} className="glass-strong p-4 hover:border-primary/50 transition-all duration-300">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="text-xs border-primary/50 text-primary">
                      {relatedJob.department}
                    </Badge>
                  </div>
                  <h3 className="font-semibold mb-2 line-clamp-2">{relatedJob.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {relatedJob.description}
                  </p>
                  <Link
                    to={`/careers/${relatedJob.id}`}
                    className="text-primary hover:text-primary/80 text-sm font-medium inline-flex items-center"
                  >
                    View Position
                    <ArrowLeft className="w-3 h-3 ml-1 rotate-180" />
                  </Link>
                </Card>
              ))}
            </div>
          </Card>
        </motion.section>
      )}

      {/* Apply Footer */}
      <Card className="glass p-6 md:p-8 text-center">
        <h2 className="text-2xl font-semibold mb-4">Ready to Join amasQIS?</h2>
        <p className="text-muted-foreground text-center mb-6">
          Take the next step in your career and help us build the future of AI
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={handleApply}
            size="lg"
            className="bg-primary text-primary-foreground hover:neon-glow px-8 py-3 cursor-pointer"
          >
            Apply for this Position
            <Briefcase className="ml-2 w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="lg"
            onClick={() => navigate('/careers')}
            className="border-primary/50 hover:border-primary hover:bg-primary/10 px-8 py-3"
          >
            View All Jobs
          </Button>
        </div>
      </Card>
    </div>
  );

  return (
    <div className="pt-20 min-h-screen">
      {/* Header */}
      <section className="py-8 border-b border-border/50">
        <div className="container mx-auto px-6">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/careers')}
              className="mb-6 hover:bg-primary/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Careers
            </Button>
            
            <div className="max-w-4xl">
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="secondary" className="bg-primary/20 text-primary">
                  {job.department}
                </Badge>
                <Badge variant="outline" className="border-accent/50 text-accent">
                  {job.type}
                </Badge>
                <Badge variant="outline" className="border-secondary/50 text-secondary">
                  {job.experience}
                </Badge>
                
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                {job.title}
              </h1>
              
              <p className="text-xl text-muted-foreground mb-6">
                {job.description}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-sm text-muted-foreground mb-6">
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-primary" />
                  {job.location}
                </div>
                <div className="flex items-center">
                  <DollarSign className="w-4 h-4 mr-2 text-primary" />
                  {job.salary}
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-primary" />
                  Posted {formatPostedDate(job.postedDate)}
                </div>
                {job.expirationDate && (
                  <div className="flex items-center">
                    <JobExpirationCountdown 
                      expirationDate={job.expirationDate}
                      className="text-sm text-muted-foreground"
                      asText={true}
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={handleApply}
                  size="lg"
                  className="bg-primary text-primary-foreground hover:neon-glow px-8 py-3 cursor-pointer"
                >
                  Apply Now
                  <Briefcase className="ml-2 w-4 h-4" />
                </Button>
                <Button variant="outline" size="lg" onClick={handleShare}>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Job
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile-First Responsive Layout */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          {/* Sidebar - Mobile First: shows before content on mobile, left column on desktop */}
          <aside className="lg:col-span-1 order-1 lg:order-1">
            <JobSidebar shouldUseSticky={true} />
          </aside>

          {/* Main Content - Mobile First: shows after sidebar on mobile, right column on desktop */}
          <main className="lg:col-span-3 order-2 lg:order-2">
            <JobMainContent />
          </main>
        </div>
      </div>

      {/* Job Application Modal */}
      <JobApplicationModal
        isOpen={isApplicationModalOpen}
        onClose={() => setIsApplicationModalOpen(false)}
        jobTitle={job.title}
        jobId={job.id}
      />
    </div>
  );
}